import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Footer from "@/components/layout/Footer";
import Hulanim from "@/components/animations/Hulanim";

const LearnWithUs = () => {
  const courses = [
    {
      title: "Partnerwork",
      subtitle: "The Language of Connection",
      description: "Discover the art of true partnership. In these sessions, we explore how timing, flow, and communication create harmony between two dancers. You will learn how to move as one, anticipate and respond naturally, and feel your partner's rhythm.",
      details: "Each lesson builds the foundation of balance, body awareness, and emotional connection that define the Klau y Ros style.",
      instructor: "Taught by Klau y Ros",
      includes: [
        "Technique drills",
        "Coordination exercises",
        "Musical interpretation"
      ],
      price: "€34.99",
      gradient: "from-aqua/20 to-pink/20"
    },
    {
      title: "Leader Styling",
      subtitle: "Command the Dance Floor",
      description: "Lead with confidence, precision, and musical intelligence. Ros teaches how to turn your lead from functional to expressive, mastering movement control, flow, and presence.",
      details: "This course sharpens your technique so every motion feels intentional and powerful, whether you are performing or social dancing.",
      instructor: "Taught by Ros",
      includes: [
        "Advanced lead techniques",
        "Body movement flow",
        "Rhythm control"
      ],
      price: "€34.99",
      gradient: "from-pink/20 to-aqua/20"
    },
    {
      title: "Follower Styling",
      subtitle: "Grace in Motion",
      description: "Unlock your inner expression. Klau's approach blends sensuality with control, focusing on posture, grace, and musical phrasing.",
      details: "You will refine how you interpret the music, shape your movements, and maintain a fluid connection with your partner. This is where dance becomes poetry in motion.",
      instructor: "Taught by Klau",
      includes: [
        "Styling drills",
        "Musicality sessions",
        "Guided improvisation"
      ],
      price: "€34.99",
      gradient: "from-aqua/20 to-pink/20"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Hulanim />
      
      {/* Hero Section */}
      <section className="hero-section relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-aqua/10 via-background to-pink/10" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(119,210,220,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(210,119,189,0.15),transparent_50%)]" />
        
        <div className="container relative z-10 text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Learn <span className="text-gradient">With Us</span>
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-aqua to-pink mx-auto mb-8" />
          </motion.div>
        </div>
      </section>

      {/* Method Section */}
      <section className="py-20 px-4">
        <div className="container max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              The <span className="text-gradient">Klau y Ros</span> Method
            </h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                Bachata is more than steps. It is connection, rhythm, and expression.
              </p>
              <p>
                Our teaching method was born from years of performing and teaching around the world, 
                refined through thousands of students who wanted more than choreography.
              </p>
              <p className="text-foreground font-medium">
                We help you feel the music, connect with your partner, and express yourself with confidence on any dance floor.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container max-w-6xl mx-auto space-y-24">
          {courses.map((course, index) => (
            <motion.div
              key={course.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className={`p-8 md:p-12 bg-gradient-to-br ${course.gradient} border-border/50 backdrop-blur-sm`}>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-3xl md:text-4xl font-bold mb-2">{course.title}</h3>
                    <p className="text-xl text-gradient font-medium">{course.subtitle}</p>
                  </div>
                  
                  <div className="space-y-4 text-muted-foreground">
                    <p className="text-lg leading-relaxed">{course.description}</p>
                    <p className="leading-relaxed">{course.details}</p>
                  </div>

                  <div className="pt-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">{course.instructor}</p>
                    
                    <div className="flex flex-wrap gap-3">
                      {course.includes.map((item) => (
                        <div key={item} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-aqua" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-6 border-t border-border/50">
                      <div>
                        <p className="text-3xl font-bold text-gradient">{course.price}</p>
                        <p className="text-sm text-muted-foreground">per month</p>
                      </div>
                      <Button className="bg-gradient-to-r from-aqua to-pink hover:opacity-90">
                        Get Started <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Special Collaborations */}
      <section className="py-20 px-4">
        <div className="container max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Special <span className="text-gradient">Collaborations</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Beyond our main series, we collaborate with top international artists to bring you special masterclasses 
              in fusion styles, performance technique, and creative choreography. These sessions change each season, 
              giving students exclusive access to world-class artistry.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Options */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Your Learning <span className="text-gradient">Options</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Individual Plan */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="p-8 h-full bg-gradient-to-br from-background to-muted/50 border-border">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Individual Course Plan</h3>
                    <p className="text-4xl font-bold text-gradient mb-2">€34.99</p>
                    <p className="text-sm text-muted-foreground">per month</p>
                  </div>

                  <p className="text-muted-foreground">
                    Choose the course that fits your focus. Each plan gives you full access to the selected series, 
                    continuous updates, and instructor feedback.
                  </p>

                  <Button variant="outline" className="w-full border-aqua text-aqua hover:bg-aqua/10">
                    Choose a Course <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </motion.div>

            {/* Premium Plan */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="p-8 h-full bg-gradient-to-br from-aqua/10 via-background to-pink/10 border-aqua/50 relative overflow-hidden">
                <div className="absolute top-4 right-4 bg-gradient-to-r from-aqua to-pink text-white text-xs font-bold px-3 py-1 rounded-full">
                  BEST VALUE
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Premium Membership</h3>
                    <p className="text-4xl font-bold text-gradient mb-2">€69.99</p>
                    <p className="text-sm text-muted-foreground">per month</p>
                  </div>

                  <div className="space-y-3">
                    <p className="font-medium">Unlock everything.</p>
                    <p className="text-muted-foreground">
                      Get full access to all three courses and every new collaboration. This plan is designed for dancers 
                      who want to experience both perspectives of connection and styling and advance together with their partners.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Perfect for dedicated learners who want to master the complete Klau y Ros method at their own pace.
                    </p>
                  </div>

                  <div className="space-y-2 pt-4">
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-aqua flex-shrink-0 mt-0.5" />
                      <span className="text-sm">Includes all future lessons and collaborations automatically</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-aqua flex-shrink-0 mt-0.5" />
                      <span className="text-sm">Best value for couples and advanced students</span>
                    </div>
                  </div>

                  <Button className="w-full bg-gradient-to-r from-aqua to-pink hover:opacity-90">
                    Go Premium <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4">
        <div className="container max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed mb-8">
              Thousands of students around the world have transformed their dancing with the Klau y Ros method. 
              Begin your journey today and experience what it means to truly feel the dance.
            </p>
            <Button size="lg" className="bg-gradient-to-r from-aqua to-pink hover:opacity-90">
              Start Your Journey <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LearnWithUs;